import { useMemo, FC, useState } from 'react';
import { Column, CellProps } from 'react-table';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router';
import { useStore } from 'hooks/useRootStore';
import { useMyTranslations } from 'hooks/useMyTranslations';
import { getDate } from 'utils/convertString';
import { PAGE_SIZE, Table } from 'components/Core/Table';
import { NoDataComponent } from 'components/Core/NoData/NoData';
import { AvatarIcon } from 'components/Core/AvatarIcon';
import { TableActionsButtons } from 'components/RequestsComponents/TableActionsButtons';
import RequestsTableFiltersForm from 'components/RequestsComponents/RequestsTableFiltersForm';
import { ICatalogueRequest } from 'stores/requests/types';
import { cataloguesRequestsTableTranslations } from './translations';
import { CataloguesRequestsTableTransFields } from './types';
import styles from './cataloguesRequestsTable.module.scss';

export const CataloguesRequestsTable: FC = observer(() => {
  const {
    requestsStore: { catalogueRequests },
  } = useStore();

  const { t } = useMyTranslations<CataloguesRequestsTableTransFields>([cataloguesRequestsTableTranslations.name]);

  const navigate = useNavigate();

  const [filtred, setFiltred] = useState<ICatalogueRequest[]>([]);

  const columns: Column<ICatalogueRequest>[] = useMemo(
    () => [
      {
        Header: t('name'),
        id: 'name',
        Cell: ({
          row: {
            original: { name, id, previewUrl },
          },
        }: CellProps<ICatalogueRequest>) => {
          return (
            <div className={styles.avatar} onClick={() => navigate(`${id}`)}>
              <AvatarIcon avatar={previewUrl} name='previewUrl' size={56} styleProps={{ borderRadius: 8 }} />
              <p className={styles.name}>{name}</p>
            </div>
          );
        },
        accessor: ({ name }) => name,
        width: 2,
      },
      {
        Header: t('createdAt'),
        id: 'createdAt',
        Cell: ({
          row: {
            original: { createdAt },
          },
        }: CellProps<ICatalogueRequest>) => <p className={styles.description}>{getDate(createdAt)}</p>,
        accessor: ({ createdAt }) => +new Date(createdAt),
        width: 1,
      },
      {
        Header: t('brand'),
        id: 'brand',
        Cell: ({
          row: {
            original: {
              brand: { name },
            },
          },
        }: CellProps<ICatalogueRequest>) => {
          return (
            <div className={styles.contacts}>
              <span className={styles.contactsPhone}>{name}</span>
            </div>
          );
        },
        accessor: ({ brand: { name } }) => name,
        width: 1,
      },
      {
        Header: t('owner'),
        id: 'owner',
        Cell: ({
          row: {
            original: {
              owner: { name, email },
            },
          },
        }: CellProps<ICatalogueRequest>) => {
          return (
            <div className={styles.contacts}>
              <span className={styles.contactsPhone}>{name}</span>
              <span className={styles.contactsEmail}>{email}</span>
            </div>
          );
        },
        accessor: ({ owner: { name } }) => name,
        width: 1,
      },
      {
        Header: t('startDate'),
        id: 'startDate',
        Cell: ({
          row: {
            original: { startDate },
          },
        }: CellProps<ICatalogueRequest>) => {
          return <p className={styles.description}>{getDate(startDate)}</p>;
        },
        accessor: ({ startDate }) => +new Date(startDate),
        width: 1,
      },
      {
        Header: t('expiredDate'),
        id: 'expiredDate',
        Cell: ({
          row: {
            original: { expiredDate },
          },
        }: CellProps<ICatalogueRequest>) => {
          return <p className={styles.description}>{getDate(expiredDate)}</p>;
        },
        accessor: ({ expiredDate }) => +new Date(expiredDate),
        width: 1,
      },
      {
        Header: t('option'),
        id: 'option',
        Cell: ({
          row: {
            original: { id, status },
          },
        }: CellProps<ICatalogueRequest>) => <TableActionsButtons id={id} entity='catalogues' status={status} />,
        width: 5,
        disableSortBy: true,
      },
    ],
    [catalogueRequests]
  );

  columns.forEach((item) => (item.Filter = () => null));

  return catalogueRequests && catalogueRequests.length > 0 ? (
    <>
      <RequestsTableFiltersForm data={catalogueRequests} setFiltredData={setFiltred} />
      <Table
        columns={columns as never}
        data={[...filtred]}
        simpleList
        isPaginated={catalogueRequests.length > PAGE_SIZE}
      />
    </>
  ) : (
    <NoDataComponent />
  );
});
